// Player settings, persisted in localStorage. Defaults reproduce the original controls exactly.
import {QualityChoice, QualityLevel, isQualityChoice, isQualityLevel} from "./quality";
import {InputModeChoice, isInputModeChoice} from "./inputMode";
import {isRuleset, Ruleset} from "./shared/rules";

export interface Settings {
    aimSensitivity: number;     // camera / aim look: radians per mouse px = aimSensitivity / 200
    strokeSensitivity: number;  // cue stroke: cue px per mouse px (also scales power for a given hand speed)
    showFps: boolean;           // the FPS counter (stats.js) in the corner of the game; hidden by default
    quality: QualityChoice;     // graphics quality (quality.ts): auto (default), low or high
    /** what auto measured on this browser (quality.ts FrameRateProbe); null until measured */
    detectedQuality: QualityLevel | null;
    /** controls (inputMode.ts): auto (default: Touch on phones and tablets, Desktop otherwise), desktop or touch */
    inputMode: InputModeChoice;
    /** the ruleset chosen for practice and Find match (shared/rules.ts RULESETS): anyball (default) or snooker */
    ruleset: Ruleset;
}

export const SETTINGS_KEY = "snooker.settings.v1";
export const DEFAULT_SETTINGS: Settings = {aimSensitivity: 1, strokeSensitivity: 1, showFps: false, quality: "auto", detectedQuality: null, inputMode: "auto", ruleset: "anyball"};
export const SENSITIVITY_MIN = 0.25;
export const SENSITIVITY_MAX = 4;

const clamp = (v: any, d: number) => {
    const n = Number(v);
    return isFinite(n) && n > 0 ? Math.min(SENSITIVITY_MAX, Math.max(SENSITIVITY_MIN, n)) : d;
};

function load(): Settings {
    try {
        const raw = JSON.parse(window.localStorage.getItem(SETTINGS_KEY) || "{}");
        return {
            aimSensitivity: clamp(raw.aimSensitivity, DEFAULT_SETTINGS.aimSensitivity),
            strokeSensitivity: clamp(raw.strokeSensitivity, DEFAULT_SETTINGS.strokeSensitivity),
            showFps: raw.showFps === true,
            quality: isQualityChoice(raw.quality) ? raw.quality : DEFAULT_SETTINGS.quality,
            detectedQuality: isQualityLevel(raw.detectedQuality) ? raw.detectedQuality : null,
            inputMode: isInputModeChoice(raw.inputMode) ? raw.inputMode : DEFAULT_SETTINGS.inputMode,
            ruleset: isRuleset(raw.ruleset) ? raw.ruleset : DEFAULT_SETTINGS.ruleset,
        };
    } catch {
        return {...DEFAULT_SETTINGS};
    }
}

function save(s: Settings) {
    try { window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)); } catch {}
}

let current: Settings = typeof window !== "undefined" ? load() : {...DEFAULT_SETTINGS};
const listeners = new Set<(s: Settings) => void>();

export const PlayerSettings = {
    get: (): Settings => current,
    set(patch: Partial<Settings>) {
        const quality = isQualityChoice(patch.quality) ? patch.quality : current.quality;
        let detectedQuality = patch.detectedQuality === null || isQualityLevel(patch.detectedQuality) ? patch.detectedQuality : current.detectedQuality;
        // choosing Auto again (from Low or High) measures again
        if (quality === "auto" && current.quality !== "auto" && patch.detectedQuality === undefined) detectedQuality = null;
        current = {
            aimSensitivity: clamp(patch.aimSensitivity ?? current.aimSensitivity, DEFAULT_SETTINGS.aimSensitivity),
            strokeSensitivity: clamp(patch.strokeSensitivity ?? current.strokeSensitivity, DEFAULT_SETTINGS.strokeSensitivity),
            showFps: typeof patch.showFps === "boolean" ? patch.showFps : current.showFps,
            quality,
            detectedQuality,
            inputMode: isInputModeChoice(patch.inputMode) ? patch.inputMode : current.inputMode,
            ruleset: isRuleset(patch.ruleset) ? patch.ruleset : current.ruleset,
        };
        save(current);
        listeners.forEach(l => l(current));
    },
    /** Back to the defaults (the ruleset too: Any ball); what auto measured on this browser is kept (it isn't a preference). */
    reset() {
        try { window.localStorage.removeItem(SETTINGS_KEY); } catch {}
        current = {...DEFAULT_SETTINGS, detectedQuality: current.detectedQuality};
        if (current.detectedQuality) save(current);
        listeners.forEach(l => l(current));
    },
    subscribe(l: (s: Settings) => void) {
        listeners.add(l);
        return () => { listeners.delete(l); };
    },
};
